import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * App Screen
 *
 * Main tabbed screen: Stream + Collections, with dynamically opened
 * per-collection tabs.
 */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/auth/auth-context";
import { blockStore } from "@/data/block-store";
import { collectionStore } from "@/data/collection-store";
import { getCollectionDetails, listCollectionChildren, listCollectionEffectiveMembers, } from "@/api/query-api";
import { collectionCollection } from "@/data/collection-collection";
import { generateId } from "@shared/types/id";
import { resolveBlockBySlug, resolveCollection, resolveName, resolveStandaloneCollection, } from "@/api/public-api";
import { useCollectionUrlBuilder, useFindCollectionBySlug, useFindStandaloneCollectionBySlug, useRouteEntity, useUrlForCollectionId, } from "@/lib/urls";
import { track } from "@/lib/analytics";
import { collectionEffectiveMembersQueryKey } from "@/api/collection-members-query-key";
import { useAccountMe } from "@/hooks/use-account-me";
import { usePrimeAccountAttributionFromMembers } from "@/hooks/use-prime-account-attribution-from-members";
import { STACK_DETAIL_PAGE_SIZE } from "@/components/stack-unfurl/stack-unfurl";
import { getLinkDescription, getLinkSiteName, getLinkTitle, } from "@shared/blocks/link/helpers";
import { fetchCollectionContextTree, } from "@/lib/copy-context";
import { Stream } from "@/components/stream/stream";
import { useStreamData } from "@/components/stream/use-stream-data";
import { pinnedStreamStore } from "@/data/pinned-stream-store";
import { useAnnotationsForBlocks } from "@/data/use-annotations";
import { userAnnotationStore } from "@/data/user-annotation-store";
import { useApp } from "@/state/app-context";
import { MainSlate } from "@/components/main-slate/main-slate";
import { SlateContent } from "@/components/slate-content/slate-content";
// Lazy-loaded: the block-detail modal subtree carries the in-house
// emoji picker which statically imports `emojibase-data/en/compact.json`
// (~540 KB) for the reaction popover. Splitting this off keeps that
// chunk (plus the modal's drawing canvas, edit shell, and toolbar
// stack) out of the homepage's cold-load bundle. Only mounted when
// the user actually has an active block selected — see
// `activeBlockId` gate below.
//
// The chunk is also *prefetched* on idle after AppScreen mounts (see
// the effect inside `AppScreenContent`). Without that prefetch the
// first click on any card paid the full chunk-fetch latency before
// the open animation could start — perceptibly stalling the very
// first card open of a session. Lifting the dynamic `import()` to a
// shared `loadBlockDetailModal()` reference lets both `React.lazy`
// and the prefetch hit webpack's module cache (idempotent).
const loadBlockDetailModal = () => import(
/* webpackChunkName: "block-detail-modal" */
/* webpackPrefetch: true */
"@/components/block-detail-modal/block-detail-modal-container");
const BlockDetailModalContainer = React.lazy(() => loadBlockDetailModal().then((m) => ({ default: m.BlockDetailModalContainer })));
/** Delay before kicking off the modal prefetch. Matches `sentry.ts`'s replay deferral so both fire after the bootstrap fetch + first paint has settled. */
const MODAL_PREFETCH_DELAY_MS = 2000;
import { ScreenContainer } from "@/components/screen-container/screen-container";
import { SlateHeader } from "@/components/slate-header/slate-header";
import { AppNav } from "@/components/app-nav/app-nav";
import { faSpinnerThird, faPlus, faGear, faChevronLeft, faMagnifyingGlass, faCalendarDays, faGrid, faMessage, faCopy, faLayerPlus, } from "@fortawesome/pro-solid-svg-icons";
import { FaIcon } from "@shared/client/components/fa-icon";
import { showToast } from "@shared/client/components/toast/show-toast";
import { CollectionSettingsModal } from "@/components/collection-settings-modal/collection-settings-modal";
import { CollectionChatSidebar } from "@/components/collection-chat-sidebar/collection-chat-sidebar";
import { GroupViewContainer } from "@/components/group-view/group-view-container";
import { MultiselectActionBar } from "@/components/multiselect-action-bar/multiselect-action-bar";
import { OmnibarHost } from "@/components/omnibar-host/omnibar-host";
import { COLLECTION_LAYER } from "@shared/types/collection";
import { useWipBorderShortcut } from "@/components/wip-border/use-wip-border-shortcut";
import { useGoBack } from "@/hooks/use-go-back";
import { openCollectionTab } from "./app-tabs-store";
import { SmartCollectionTabContent } from "./smart-collection-tab-content";
import { collectionViewModeStore, } from "@/state/collection-view-mode-store";
import { SHORTCUTS } from "@/keyboard/shortcuts";
import { useShortcut } from "@/keyboard/use-shortcut";
import "./app-screen.scss";
// ---------------------------------------------------------------------------
// Stream tab content
// ---------------------------------------------------------------------------
const APP_TITLE = "Putty";
function formatDocumentTitle(pageTitle) {
    if (process.env.NODE_ENV !== "production") {
        const devLabel = process.env.WORKTREE_NAME || "DEV";
        return `[${devLabel}] ${pageTitle}`;
    }
    return pageTitle;
}
const StreamTabContent = memo(() => {
    const streamData = useStreamData({});
    // Keep the homepage stream's id list aligned with pinned-block ids
    // so the pinned section's lookup against `streamData.items` always
    // finds the envelope. `useList` filters strictly to the stream's
    // own `blockIds`, so a pin made in another tab (or any pin whose
    // envelope lives outside the loaded window) needs to be threaded
    // through explicitly.
    pinnedStreamStore.useSyncToMainStream();
    // The user's Primary Group is the parent we pass to the
    // drag-to-create-stack gesture. Stacks can now sit directly under a
    // Group (relaxed 2026-05-21 — see `LEGAL_PARENT_CHILD_LAYER_PAIRS`),
    // so dropping a block on another block in the homepage feed creates
    // a fresh Stack parented to Primary. Until Primary's id resolves
    // from cache (effectively only the first paint after a cold sign-in),
    // `createStackOnDropParentId` stays undefined and the gesture is a
    // silent no-op — matching the pre-relaxation behavior of the home
    // feed.
    const primaryGroupId = collectionStore.usePrimaryGroupId();
    // No top-level loading gate: the pinned section's data is
    // independent of the main feed's `/v1/block/list` seed (pin order
    // comes from localStorage / bootstrap, envelopes from IDB-hydrated
    // `blockCollection`). Hiding the whole `<Stream>` behind
    // `streamData.isLoading` made pinned blocks wait for a network
    // call they don't need. The Stream's own `stream__empty` block
    // handles the "no data at all" case once the seed completes empty.
    return (_jsx(Stream, { data: streamData, columns: 2, groupBy: "day", surfaceKey: "stream:home", analyticsSurface: "homepage", enablePinnedSection: true, createStackOnDropParentId: primaryGroupId, 
        // Collapse runs of 2+ blocks on the same day that share the same
        // non-primary `collectionId` into a single synthetic Stack tile.
        // Re-uses the real Stack UI: clicking the tile unfurls *just*
        // those grouped blocks inline. See `mergeSameCollectionBlocks`.
        groupSameCollectionPerDay: true, 
        // Paint Stack tiles with a dark, palette-derived card shell that
        // matches the source Collection's own `<CollectionBlockView>`.
        // On the home feed each stack — synthetic or real — is *about*
        // its source Collection (synthetic stacks group blocks by parent
        // collection; real Stacks live inside one); the dark shell makes
        // that source unmissable. Inside a Collection's own view stacks
        // keep the default light styling so they nest visually rather
        // than competing with the containing Collection's dark surface.
        darkStackTiles: true }));
});
// ---------------------------------------------------------------------------
// Collections tab content
// ---------------------------------------------------------------------------
const CollectionsTabContent = memo(() => {
    const navigate = useNavigate();
    const { collections, isLoading } = collectionStore.useList();
    const buildCollectionUrl = useCollectionUrlBuilder();
    const buildUrlForCollectionId = useUrlForCollectionId();
    const [isCreating, setIsCreating] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const [createPending, setCreatePending] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const visibleCollections = useMemo(() => {
        // The Collections tab surfaces layer-1 Collections only. Groups
        // (layer 0) live in the switcher, Stacks (layer 2) live inside
        // their parent Collection, and primary collections are hidden
        // from the list view per the existing convention.
        const layer1 = collections.filter((c) => c.type !== "primary" && (c.layerIndex ?? 1) === COLLECTION_LAYER.collection);
        const query = searchQuery.trim().toLowerCase();
        if (query.length === 0)
            return layer1;
        return layer1.filter((c) => (c.title ?? "Untitled").toLowerCase().includes(query));
    }, [collections, searchQuery]);
    const handleOpenCollection = useCallback((collection) => {
        track({ name: "collection.opened", props: { source: "card" } });
        // Defensive: only layer-1 Collections are tab-eligible. The list
        // is already filtered, but guarding here keeps the rule local in
        // case a future caller routes a non-layer-1 row through this
        // handler.
        if ((collection.layerIndex ?? 1) !== COLLECTION_LAYER.collection) {
            navigate(buildCollectionUrl(collection));
            return;
        }
        openCollectionTab({
            collectionId: collection.id,
            title: collection.title ?? "Untitled",
        });
        navigate(buildCollectionUrl(collection));
    }, [navigate, buildCollectionUrl]);
    const handleToggleCreate = useCallback(() => {
        setIsCreating((prev) => {
            if (prev)
                setNewTitle("");
            return !prev;
        });
    }, []);
    const handleCreateSubmit = useCallback(async (e) => {
        e.preventDefault();
        const trimmed = newTitle.trim();
        if (trimmed.length === 0 || createPending)
            return;
        setCreatePending(true);
        try {
            const id = await collectionStore.create({ title: trimmed });
            setIsCreating(false);
            setNewTitle("");
            openCollectionTab({ collectionId: id, title: trimmed });
            navigate(buildUrlForCollectionId(id));
        }
        catch {
            showToast("Failed to create collection");
        }
        finally {
            setCreatePending(false);
        }
    }, [newTitle, createPending, navigate, buildUrlForCollectionId]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === "Escape") {
            setIsCreating(false);
            setNewTitle("");
        }
    }, []);
    const toolbar = (_jsxs("div", { className: "app-collections__toolbar", children: [_jsxs("div", { className: "app-collections__toolbar-row", children: [_jsxs("button", { type: "button", className: `app-collections__new-btn ${isCreating ? "app-collections__new-btn--active" : ""}`, onClick: handleToggleCreate, children: [_jsx(FaIcon, { icon: faPlus }), _jsx("span", { children: "New" })] }), _jsxs("div", { className: "app-collections__search", children: [_jsx(FaIcon, { icon: faMagnifyingGlass, className: "app-collections__search-icon" }), _jsx("input", { type: "text", className: "app-collections__search-input", placeholder: "Filter collections...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), onKeyDown: (e) => {
                                    if (e.key === "Escape" && searchQuery.length > 0) {
                                        e.preventDefault();
                                        setSearchQuery("");
                                    }
                                }, "aria-label": "Filter collections" })] })] }), isCreating ? (_jsxs("form", { className: "app-collections__create-form", onSubmit: handleCreateSubmit, children: [_jsx("input", { type: "text", className: "app-collections__create-input", placeholder: "Collection name...", value: newTitle, onChange: (e) => setNewTitle(e.target.value), onKeyDown: handleKeyDown, maxLength: 200, autoFocus: true, disabled: createPending }), _jsx("button", { type: "submit", className: "app-collections__create-submit", disabled: newTitle.trim().length === 0 || createPending, children: createPending ? "..." : "Create" })] })) : null] }));
    const body = (() => {
        if (isLoading && visibleCollections.length === 0) {
            return (_jsx("div", { className: "loading-spinner-container", children: _jsx(FaIcon, { icon: faSpinnerThird, className: "loading-spinner" }) }));
        }
        if (visibleCollections.length === 0) {
            const message = searchQuery.trim().length > 0
                ? `No collections match "${searchQuery.trim()}".`
                : "No collections yet.";
            return _jsx("p", { className: "app-screen__status", children: message });
        }
        return (_jsx(Stream, { data: {
                items: [],
                collections: visibleCollections,
                isLoading: false,
                hasMore: false,
                isFetchingNextPage: false,
                fetchMore: () => { },
            }, columns: 2, groupBy: "day", onOpenCollection: handleOpenCollection, surfaceKey: "collections:list", pinScope: { type: "account" }, pinSurface: "tab", pinSlotLabel: "top of list" }));
    })();
    return (_jsxs("div", { className: "app-collections", children: [toolbar, _jsx("div", { className: "app-collections__body", children: body })] }));
});
// ---------------------------------------------------------------------------
// Collection detail tab content
// ---------------------------------------------------------------------------
const COLLECTION_DETAIL_PAGE_SIZE = 100;
const MAX_COLLECTION_FILTER_QUERY_LENGTH = 200;
const MAX_FILTER_FIELD_CHARS = 1000;
const MAX_FILTER_AGGREGATE_CHARS = 5000;
function pushFilterField(parts, value) {
    if (value === undefined)
        return;
    const trimmed = value.trim();
    if (trimmed.length === 0)
        return;
    const bounded = trimmed.length > MAX_FILTER_FIELD_CHARS
        ? trimmed.slice(0, MAX_FILTER_FIELD_CHARS)
        : trimmed;
    parts.push(bounded);
}
function buildCollectionFilterText(collection) {
    const parts = [];
    pushFilterField(parts, collection.title);
    pushFilterField(parts, collection.summary);
    pushFilterField(parts, collection.slug);
    pushFilterField(parts, collection.metadata.emoji);
    const joined = parts.join(" ");
    const bounded = joined.length > MAX_FILTER_AGGREGATE_CHARS
        ? joined.slice(0, MAX_FILTER_AGGREGATE_CHARS)
        : joined;
    return bounded.toLowerCase();
}
function buildBlockFilterText(envelope) {
    const block = envelope.block;
    const parts = [];
    pushFilterField(parts, block.title);
    pushFilterField(parts, block.summary);
    pushFilterField(parts, block.context);
    pushFilterField(parts, block.metadata.source?.domain);
    pushFilterField(parts, block.metadata.source?.url);
    switch (block.type) {
        case "text": {
            const content = block.content;
            pushFilterField(parts, content.title);
            pushFilterField(parts, content.text);
            break;
        }
        case "note": {
            const content = block.content;
            pushFilterField(parts, content.title);
            pushFilterField(parts, content.noteType);
            pushFilterField(parts, content.journalDate);
            break;
        }
        case "link": {
            const content = block.content;
            pushFilterField(parts, content.url);
            pushFilterField(parts, content.domain);
            pushFilterField(parts, content.subtype);
            pushFilterField(parts, getLinkTitle(content));
            pushFilterField(parts, getLinkSiteName(content));
            pushFilterField(parts, getLinkDescription(content));
            break;
        }
        case "email": {
            const content = block.content;
            pushFilterField(parts, content.subject);
            pushFilterField(parts, content.from.address);
            pushFilterField(parts, content.from.name);
            pushFilterField(parts, content.bodyText);
            for (const recipient of content.to) {
                pushFilterField(parts, recipient.address);
                pushFilterField(parts, recipient.name);
            }
            for (const recipient of content.cc ?? []) {
                pushFilterField(parts, recipient.address);
                pushFilterField(parts, recipient.name);
            }
            break;
        }
    }
    const joined = parts.join(" ");
    const bounded = joined.length > MAX_FILTER_AGGREGATE_CHARS
        ? joined.slice(0, MAX_FILTER_AGGREGATE_CHARS)
        : joined;
    return bounded.toLowerCase();
}
const CollectionDetailTabContent = memo(({ collectionId }) => {
    const navigate = useNavigate();
    const goBack = useGoBack();
    const app = useApp();
    const buildCollectionUrl = useCollectionUrlBuilder();
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isCreatingStack, setIsCreatingStack] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const [filterQuery, setFilterQuery] = useState("");
    const filterInputRef = useRef(null);
    const normalizedFilterQuery = useMemo(() => filterQuery.trim().toLowerCase(), [filterQuery]);
    useEffect(() => {
        setFilterQuery("");
    }, [collectionId]);
    useShortcut(SHORTCUTS.omnibarFocusAsk, () => {
        const input = filterInputRef.current;
        if (input === null)
            return;
        input.focus();
        input.select();
    }, {
        enabled: () => filterInputRef.current !== null,
    });
    // View mode is persisted per-collection via `collectionViewModeStore`
    // (localStorage; see that file for the future cross-device migration
    // plan). Reading here gives us automatic cross-tab updates if the same
    // collection is open in another window.
    const viewMode = collectionViewModeStore.useMode(collectionId);
    const setViewMode = useCallback((next) => collectionViewModeStore.setMode(collectionId, next), [collectionId]);
    // When any card in this view is selected, dim the non-selected ones so the
    // selected card reads as the focus. The actual dim / hover animation lives
    // in the SCSS under `.app-collection--has-selection`.
    const hasSelection = app.useStore((s) => s.selection.size > 0);
    // Per-collection block index. Rows are pointers (blockId, sortKey);
    // each card resolves its own envelope via `blockStore.useById` (Design
    // B). `loadBlocks` populates the index on mount and on `fetchNextPage`;
    // optimistic membership writes from `copyBlocks` / `blockStore.add`
    // appear here instantly via TanStack DB's live query.
    const blocksList = collectionStore.useBlocks(collectionId);
    useEffect(() => {
        void collectionStore.loadBlocks(collectionId, { limit: COLLECTION_DETAIL_PAGE_SIZE });
    }, [collectionId]);
    // Children query: a Collection's direct children are layer-2 Stacks
    // (and any other layer-2+ children). Render them as cards alongside
    // the collection's direct blocks so users can drill into a Stack
    // without leaving the parent's grid.
    const childrenQuery = useQuery({
        queryKey: ["app.collectionChildren", collectionId],
        queryFn: () => listCollectionChildren(collectionId),
        staleTime: 30_000,
    });
    // Prefetch each Stack's children alongside the parent's load so the
    // inline unfurl opens instantly — no visible loading round-trip when
    // the user clicks a stack tile. The query key + page size MUST match
    // `useStackUnfurl`'s `useQuery` exactly (`["stream.stackChildren",
    // id]`, `limit: STACK_DETAIL_PAGE_SIZE`); the hook then hits cache
    // on click and the `loading -> opening` transition resolves in the
    // same render tick.
    //
    // Gate on `blockCount > 0`. Two reasons:
    //   1. Race-safety. The multiselect "Create stack" path optimistically
    //      inserts a placeholder Stack tile (blockCount: 0) into this same
    //      `app.collectionChildren` cache BEFORE the create POST commits
    //      on the server. Firing the prefetch on the placeholder id would
    //      lose the race and hit `/collection/:placeholderId/details`
    //      against a row that doesn't exist yet — `canRead` returns false
    //      for missing rows (`effective_collection_role` seed produces no
    //      ancestors), surfacing as a noisy `ForbiddenError("No access to
    //      this collection")` in Sentry. After the move completes and
    //      invalidates `app.collectionChildren`, the refetch returns the
    //      Stack with its real blockCount and the prefetch fires cleanly.
    //   2. Cost. An empty Stack has nothing to prefetch — the unfurl-on-click
    //      round-trip is identical work, and skipping it here saves a GET
    //      per empty Stack on every parent-Collection load.
    const queryClient = useQueryClient();
    useEffect(() => {
        const stacks = (childrenQuery.data ?? []).filter((c) => c.layerIndex === COLLECTION_LAYER.stack && (c.blockCount ?? 0) > 0);
        for (const stack of stacks) {
            void queryClient.prefetchQuery({
                queryKey: ["stream.stackChildren", stack.id],
                queryFn: async () => {
                    const data = await getCollectionDetails(stack.id, { limit: STACK_DETAIL_PAGE_SIZE });
                    // Mirror envelopes into `blockCollection` so the unfurl's
                    // per-card `blockStore.useById` resolves on first render. The
                    // hook's own queryFn does the same on cold-cache fetches; this
                    // covers the prefetched / cache-hit path so opening the stack
                    // doesn't flash the pending-skeleton spinner. See
                    // `stack-unfurl.tsx` for the full rationale.
                    blockStore.cacheEnvelopes(data.blocks);
                    // Warm annotation rows for the same prefetched children so
                    // stickers / stamps / drawings are available on the first
                    // stack open (cache-hit path). Without this, stack child
                    // envelopes are warm but annotations remain cold until a
                    // later explicit warm pass.
                    void userAnnotationStore.ensureLoadedForBlocks(data.blocks.map((envelope) => envelope.block.id));
                    return data;
                },
                staleTime: 30_000,
            });
        }
    }, [childrenQuery.data, queryClient]);
    // Collection metadata lives in the cross-layer cache, populated by
    // either the periodic `listCollections` refresh OR the metadata
    // upsert inside `collectionStore.loadBlocks` — whichever happens
    // first.
    const collection = collectionStore.useFindById(collectionId);
    const headerTitle = collection?.title ?? "Untitled";
    const collectionPageTitle = useMemo(() => {
        const trimmedTitle = headerTitle.trim();
        const safeTitle = trimmedTitle.length > 0 ? trimmedTitle : "Untitled";
        return formatDocumentTitle(`${safeTitle} · ${APP_TITLE}`);
    }, [headerTitle]);
    // Keep the browser tab title aligned with the currently-open
    // collection. On unmount (navigating away from a collection route)
    // restore the app-level default title.
    useEffect(() => {
        document.title = collectionPageTitle;
    }, [collectionPageTitle]);
    useEffect(() => {
        return () => {
            document.title = formatDocumentTitle(APP_TITLE);
        };
    }, []);
    // Promote the tab to the front on mount / keep title in sync if the
    // collection list arrives after the detail load. Only layer-1
    // Collections live in the nav tab strip — Stacks unfurl inline
    // inside their parent's grid, and Groups have their own switcher.
    // Wait for `collection` to land so we can gate on layerIndex;
    // without the gate, hitting `/collection/:stackId` directly would
    // push the stack into the tab bar, breaking the "Stacks aren't
    // tabs" rule.
    useEffect(() => {
        if (!collection)
            return;
        if (collection.layerIndex !== COLLECTION_LAYER.collection)
            return;
        openCollectionTab({ collectionId, title: headerTitle });
    }, [collection, collectionId, headerTitle]);
    // Publish the active selection container so the multiselect bar can
    // surface "Create stack" / "Remove from stack" based on the layer of
    // the collection whose blocks are being selected. Wait until the
    // collection's layerIndex is known — falling back to layer 1 by
    // default would mis-classify Stacks and offer the wrong action.
    useEffect(() => {
        if (!collection)
            return;
        app.setSelectionContainer({
            collectionId,
            layerIndex: collection.layerIndex ?? 1,
        });
        return () => {
            app.setSelectionContainer(undefined);
        };
    }, [app, collectionId, collection]);
    // Warm the user-annotation collection for the blocks rendered below.
    const blockIds = useMemo(() => blocksList.rows.map((r) => r.blockId), [blocksList.rows]);
    useAnnotationsForBlocks(blockIds);
    const filteredBlockRows = useMemo(() => {
        if (normalizedFilterQuery.length === 0)
            return blocksList.rows;
        return blocksList.rows.filter((row) => {
            const envelope = blockStore.get(row.blockId);
            if (envelope === undefined)
                return false;
            return buildBlockFilterText(envelope).includes(normalizedFilterQuery);
        });
    }, [blocksList.rows, normalizedFilterQuery]);
    const childCollections = childrenQuery.data ?? [];
    const filteredChildCollections = useMemo(() => {
        if (normalizedFilterQuery.length === 0)
            return childCollections;
        return childCollections.filter((child) => buildCollectionFilterText(child).includes(normalizedFilterQuery));
    }, [childCollections, normalizedFilterQuery]);
    // Prime per-block creator avatars on shared collection views.
    //
    // The cards rendered below resolve their creator's avatar via
    // `useAccountIdentity(block.createdBy)` (see `BlockViewContainer`),
    // which reads from the shared `accountAttributionStore`. That store is
    // primed by `usePrimeAccountAttributionFromMembers` whenever a list of
    // effective members loads — so loading them once here, at the
    // collection level, hydrates every card's avatar in a single network
    // round-trip instead of fanning out per-card.
    //
    // Detection mirrors `collection-settings-modal.tsx`: a layer-1
    // Collection whose parent is NOT Primary covers both standalone
    // (no parent — the parent lookup returns undefined, which fails
    // `?.type === "primary"`) and Group-rooted shared collections. The
    // user's own Primary-rooted Collections are private and don't need
    // attribution avatars.
    const collectionParent = collectionStore.useFindById(collection?.parentCollectionId);
    const isSharedCollection = collection !== undefined &&
        collection.layerIndex === COLLECTION_LAYER.collection &&
        collectionParent?.type !== "primary";
    // Breadcrumb context — surfaces "where am I?" for the current
    // collection/stack by walking one or two layers up the parent chain.
    // We look up `collectionGrandparent` (parent of `collectionParent`)
    // so a Stack inside a Collection inside a Group can read "Group / Collection"
    // above its own title, while a layer-1 Collection directly under a
    // Group / Primary reads just "Group" or "@username".
    //
    // Primary parents are replaced with the signed-in user's `@username`
    // (Primaries are private to the owner, so the current viewer is always
    // the owner). Standalone collections (`parentCollectionId === undefined`)
    // get no breadcrumb — they live in their own ACL bubble with no
    // workspace context to surface.
    const collectionGrandparent = collectionStore.useFindById(collectionParent?.parentCollectionId);
    const accountMe = useAccountMe();
    const breadcrumb = useMemo(() => {
        if (collectionParent === undefined)
            return [];
        const ancestors = [];
        if (collectionGrandparent !== undefined)
            ancestors.push(collectionGrandparent);
        ancestors.push(collectionParent);
        const crumbs = [];
        for (const ancestor of ancestors) {
            if (ancestor.type === "primary") {
                const username = accountMe.account?.username;
                if (username === undefined || username.length === 0)
                    continue;
                crumbs.push({
                    key: ancestor.id,
                    label: `@${username}`,
                    href: `/${username}`,
                });
                continue;
            }
            crumbs.push({
                key: ancestor.id,
                label: ancestor.title ?? "Untitled",
                href: buildCollectionUrl(ancestor),
            });
        }
        return crumbs;
    }, [
        collectionParent,
        collectionGrandparent,
        accountMe.account?.username,
        buildCollectionUrl,
    ]);
    const effectiveMembersQuery = useQuery({
        queryKey: collectionEffectiveMembersQueryKey(collectionId),
        queryFn: () => listCollectionEffectiveMembers(collectionId),
        enabled: isSharedCollection,
        staleTime: 15_000,
    });
    usePrimeAccountAttributionFromMembers(effectiveMembersQuery.data, queryClient, collectionId, effectiveMembersQuery.dataUpdatedAt, isSharedCollection);
    const handleCollectionUpdated = useCallback((updated) => {
        // Mirror the layer-1 gate from the mount-time openCollectionTab
        // call so a rename of a Stack via the settings modal doesn't
        // accidentally promote it to the tab strip.
        if (updated.layerIndex === COLLECTION_LAYER.collection) {
            openCollectionTab({ collectionId, title: updated.title ?? "Untitled" });
        }
        void collectionStore.refreshCollections();
    }, [collectionId]);
    // Create an empty Stack as a child of this Collection. Mirrors the
    // optimistic-create path used by the drag-to-stack and multiselect
    // "Create stack" flows (`createStackFromBlocks`), minus the move /
    // Haiku-rename steps — there are no source blocks to ground a
    // suggestion on.
    //
    // Latency model — every visible piece of UI lands at T+0:
    //
    //   1. Pre-allocate the stack id on the client (`generateId`) so
    //      every optimistic write below — including the auto-open +
    //      title-edit signal — keys against the canonical (eventual
    //      server) id.
    //   2. Publish `pendingNewStackId` BEFORE kicking off `createStack`.
    //      The signal lands in the Zustand store first, so when the
    //      optimistic write inside `createStack` synchronously mounts
    //      the new `<StackBlockView>`, the tile's mount-time effect
    //      observes the signal on its very first render — no flash of
    //      "closed tile, then it opens".
    //   3. Pre-seed `["stream.stackChildren", newStackId]` with an
    //      empty result. Without this, the moment the StackBlockView
    //      effect calls `onOpen` → `useStackUnfurl.toggleStack` →
    //      `setOpenStackId`, the unfurl's `useQuery` would race the
    //      in-flight create POST and 404 against a row the server
    //      hasn't created yet — pinning the unfurl in `phase: loading`
    //      for ~1s until React Query's retry caught up. Writing the
    //      cache here lets the unfurl resolve from cache instantly and
    //      transition straight to `opening` / `open` for a stack we
    //      already know is empty.
    //   4. Kick off `createStack` (don't await for the open/edit UX
    //      to settle — its sync part runs the optimistic write inside
    //      the same tick; the network POST settles later).
    //
    // Failure path: roll back the cache pre-seed + the signal so a
    // failed create doesn't leave a phantom stack id pinned anywhere.
    // The optimistic placeholder rollback is handled inside `createStack`'s
    // own catch.
    //
    // The toast is intentionally replaced by the inline edit affordance:
    // the user sees the result instantly — open tile + focused input —
    // without a separate confirmation pill.
    const handleCreateEmptyStack = useCallback(async () => {
        if (isCreatingStack)
            return;
        setIsCreatingStack(true);
        const newStackId = generateId("collection");
        // 1. Signal first so the tile's mount-time effect sees it on
        //    the same render the optimistic write below produces.
        app.requestNewStackOpenAndEdit(newStackId);
        // 2. Synchronously kick off `createStack` so its optimistic
        //    writes hit the cache in the same tick as the signal.
        //    `clientId` makes the placeholder row use our pre-allocated
        //    id verbatim.
        const createPromise = collectionStore.createStack(collectionId, {
            title: "Untitled stack",
            clientId: newStackId,
        });
        // 3. Pre-seed the unfurl's children cache. The placeholder is
        //    in `collectionCollection` now (the write inside `createStack`
        //    above ran synchronously); we mirror it here as the
        //    `collection` field so the cache entry typechecks as
        //    `CollectionDetailsData`. Empty `blocks: []` reflects what
        //    we know — a freshly-created stack has no members yet.
        const placeholder = collectionCollection.get(newStackId);
        if (placeholder !== undefined) {
            queryClient.setQueryData(["stream.stackChildren", newStackId], {
                collection: placeholder,
                blocks: [],
                count: 0,
            });
        }
        try {
            track({
                name: "collection.stack_created",
                props: { source: "empty_button", block_count: 0 },
            });
            await createPromise;
        }
        catch (e) {
            // Roll back EVERY surface we wrote at T+0 so a failed create
            // doesn't leave a half-applied state.
            app.clearPendingNewStack();
            queryClient.removeQueries({
                queryKey: ["stream.stackChildren", newStackId],
            });
            showToast.error(e instanceof Error ? e.message : "Failed to create stack");
        }
        finally {
            setIsCreatingStack(false);
        }
    }, [app, collectionId, isCreatingStack, queryClient]);
    // Serialize the collection — full metadata + every block in this
    // collection AND every nested subcollection (Stacks, sub-Stacks, …)
    // — into a JSON snippet a user can paste into another tool (LLM
    // chat, doc, ticket) as context for follow-up work.
    //
    // What's included:
    //   - Collection header (id, title, summary, type, emoji, slug, …)
    //   - `blocks` — per-block title / summary / context / capturedAt /
    //     source URL / type-specific content snippet
    //   - `childCollections` — recursive: each entry is a full node
    //     with its own `blocks` + `childCollections` so the entire
    //     subtree is reproduced. Walks to every depth via
    //     `listCollectionChildren`; cycle-safe via a visited set even
    //     though the layer model precludes cycles.
    //
    // What's intentionally NOT included:
    //   - Membership / ACL rows (different surface — settings modal)
    //   - File / thumbnail references, EXIF, ingest pipeline bookkeeping
    //     (stripped inside the per-block summarizer)
    //
    // No block cap — the prior `MAX_BLOCKS_IN_COPY_CONTEXT = 500`
    // truncation was removed by request. A pathological tree can
    // produce a multi-MB paste; that's the caller's trade-off.
    //
    // Fetches go through the query client so a repeated copy within
    // ~30s reuses cached pages instead of re-hitting the API. The
    // button shows a spinner while the walk is in flight; we don't
    // optimistically use the current `blocksList.rows` because that
    // would only cover what the user has scrolled into view —
    // contradicting "no limit".
    const handleCopyContext = useCallback(async () => {
        if (!collection || isCopying)
            return;
        setIsCopying(true);
        try {
            const counts = { blocks: 0, collections: 0 };
            const tree = await fetchCollectionContextTree(queryClient, collectionId, collection, counts);
            // Assemble the root-level payload. Optional fields are left
            // as `undefined` so `JSON.stringify` drops them — a paste with
            // no `slug` / `publishedAt` / etc. is cleaner than one carrying
            // explicit `null` placeholders.
            const payload = {
                id: collection.id,
                type: collection.type,
                createdAt: collection.createdAt,
                blocks: tree.blocks,
                childCollections: tree.childCollections,
            };
            if (collection.title)
                payload.title = collection.title;
            if (collection.summary)
                payload.summary = collection.summary;
            if (collection.metadata.emoji)
                payload.emoji = collection.metadata.emoji;
            if (collection.metadata.color)
                payload.color = collection.metadata.color;
            if (collection.slug)
                payload.slug = collection.slug;
            if (collection.layerIndex !== undefined)
                payload.layerIndex = collection.layerIndex;
            if (collection.parentCollectionId)
                payload.parentCollectionId = collection.parentCollectionId;
            if (collection.publishedAt)
                payload.publishedAt = collection.publishedAt;
            if (collection.blockCount !== undefined)
                payload.blockCount = collection.blockCount;
            if (collection.lastAddedAt)
                payload.lastAddedAt = collection.lastAddedAt;
            const json = JSON.stringify(payload, undefined, 2);
            await navigator.clipboard.writeText(json);
            const parts = [
                `${counts.blocks} block${counts.blocks === 1 ? "" : "s"}`,
            ];
            if (counts.collections > 0) {
                parts.push(`${counts.collections} subcollection${counts.collections === 1 ? "" : "s"}`);
            }
            showToast.success(`Copied (${parts.join(", ")})`);
        }
        catch (e) {
            const msg = e instanceof Error ? e.message : "Unknown error";
            showToast.error(`Failed to copy context: ${msg}`);
        }
        finally {
            setIsCopying(false);
        }
    }, [collection, collectionId, isCopying, queryClient]);
    const header = collection ? (_jsxs("div", { className: "app-collection-header", children: [_jsxs("div", { className: "app-collection-header__title-group", children: [_jsx("button", { type: "button", className: "app-collection-header__back", onClick: goBack, "aria-label": "Back", children: _jsx(FaIcon, { icon: faChevronLeft }) }), _jsxs("div", { className: "app-collection-header__title-stack", children: [breadcrumb.length > 0 ? (_jsx("nav", { className: "app-collection-header__breadcrumb", "aria-label": "Breadcrumb", children: breadcrumb.map((crumb, i) => (_jsxs(React.Fragment, { children: [i > 0 ? (_jsx("span", { className: "app-collection-header__breadcrumb-sep", "aria-hidden": "true", children: "/" })) : null, crumb.href !== undefined ? (_jsx(Link, { to: crumb.href, className: "app-collection-header__breadcrumb-link", children: crumb.label })) : (_jsx("span", { className: "app-collection-header__breadcrumb-text", children: crumb.label }))] }, crumb.key))) })) : null, _jsx("h1", { className: "app-collection-header__title", children: headerTitle })] })] }), _jsxs("div", { className: "app-collection-header__actions", children: [_jsxs("div", { className: "app-collection-header__view-toggle", role: "group", "aria-label": "View mode", children: [_jsx("button", { type: "button", className: "app-collection-header__view-toggle-btn", "data-active": viewMode === "chronological" ? "" : undefined, onClick: () => setViewMode("chronological"), "aria-label": "Chronological view", "aria-pressed": viewMode === "chronological", title: "Chronological", children: _jsx(FaIcon, { icon: faCalendarDays }) }), _jsx("button", { type: "button", className: "app-collection-header__view-toggle-btn", "data-active": viewMode === "grid" ? "" : undefined, onClick: () => setViewMode("grid"), "aria-label": "Grid view", "aria-pressed": viewMode === "grid", title: "Grid", children: _jsx(FaIcon, { icon: faGrid }) })] }), collection?.layerIndex === COLLECTION_LAYER.collection ? (_jsx("button", { type: "button", className: "app-collection-header__action app-collection-header__action--icon", onClick: () => void handleCreateEmptyStack(), disabled: isCreatingStack, "aria-label": "Create empty stack", title: "Create empty stack", children: _jsx(FaIcon, { icon: isCreatingStack ? faSpinnerThird : faLayerPlus, spin: isCreatingStack }) })) : null, _jsx("button", { type: "button", className: "app-collection-header__action app-collection-header__action--icon", onClick: () => setIsChatOpen(true), "aria-label": "Ask about this collection", title: "Ask about this collection", children: _jsx(FaIcon, { icon: faMessage }) }), _jsx("button", { type: "button", className: "app-collection-header__action app-collection-header__action--icon", onClick: () => void handleCopyContext(), disabled: isCopying, "aria-label": "Copy collection context as JSON", title: "Copy collection context as JSON", children: _jsx(FaIcon, { icon: isCopying ? faSpinnerThird : faCopy, spin: isCopying }) }), _jsx("button", { type: "button", className: "app-collection-header__action app-collection-header__action--icon", onClick: () => setIsSettingsOpen(true), "aria-label": "Collection settings", children: _jsx(FaIcon, { icon: faGear }) })] })] })) : null;
    const handleOpenChildCollection = useCallback((child) => {
        // Stacks (layer 2) are intercepted inside `<Stream>` and
        // unfurled inline — they should never reach this handler.
        // Defensive route-out in case a future caller leaks one through:
        // we'd rather navigate to the Stack as if it were a Collection
        // than render nothing.
        navigate(buildCollectionUrl(child));
    }, [navigate, buildCollectionUrl]);
    // The unfurl mutates nothing today (drag-into-unfurl is a tracked
    // follow-up), and any membership writes from inside the unfurl
    // (e.g. multiselect "Remove from stack") propagate to this view via
    // `collectionStore`'s live query — no explicit refetch needed. The
    // v0 modal called `invalidateBlocks` on close, which dropped the
    // parent's index and flashed an empty grid for a tick before
    // reloading; that's the visible "load state on close" the inline
    // unfurl now avoids.
    const body = (() => {
        if (blocksList.isLoading && blocksList.rows.length === 0) {
            return (_jsx("div", { className: "loading-spinner-container", children: _jsx(FaIcon, { icon: faSpinnerThird, className: "loading-spinner" }) }));
        }
        if (filteredBlockRows.length === 0 && filteredChildCollections.length === 0) {
            if (normalizedFilterQuery.length > 0) {
                return _jsxs("p", { className: "app-screen__status", children: ["No stacks or blocks match \"", filterQuery.trim(), "\"."] });
            }
            return _jsx("p", { className: "app-screen__status", children: "This collection is empty." });
        }
        // The Stream consumer still expects `items: SimpleBlockEnvelope[]`
        // for grouping by `block.surfaceAt`. We synthesize minimal
        // envelopes from the block-index rows + the cached real
        // envelopes (resolved via `blockStore.get`), so each row has the
        // sortKey the grouping algorithm needs. Cards self-resolve full
        // envelope content via `useById` (Design B); this synth is only
        // used for layout decisions.
        const items = filteredBlockRows.map((row) => {
            const cached = blockStore.get(row.blockId);
            if (cached)
                return cached;
            // Fallback stub for the brief window before useById resolves.
            // The card renders the pending skeleton in this state.
            return {
                block: {
                    id: row.blockId,
                    type: "text",
                    content: { text: "" },
                    collectionId: "",
                    createdBy: "",
                    lineageId: "",
                    title: "",
                    summary: "",
                    metadata: { channel: "viewer_client" },
                    ingestorVersion: "0",
                    processingStatus: "pending",
                    capturedAt: row.sortKey,
                    createdAt: row.sortKey,
                    surfaceAt: row.sortKey,
                },
            };
        });
        // `createStackOnDropParentId` opts the surface into the
        // drag-to-create-stack gesture: dragging a block onto another
        // block here creates a new Stack containing both blocks, parented
        // under this surface. Layer-1 Collections and layer-0 Groups can
        // both host Stacks under the relaxed model (2026-05-21 — see
        // `LEGAL_PARENT_CHILD_LAYER_PAIRS`), so we accept both. The Stack
        // interior (this same component mounted for a layer-2 id)
        // explicitly opts OUT so dropping inside an already-open Stack
        // is a silent no-op rather than a server-side reject.
        const allowStackOnDrop = collection?.layerIndex === COLLECTION_LAYER.collection ||
            collection?.layerIndex === COLLECTION_LAYER.group
            ? collectionId
            : undefined;
        return (_jsx(Stream, { data: {
                items,
                collections: filteredChildCollections,
                isLoading: blocksList.isLoading,
                hasMore: blocksList.hasMore,
                isFetchingNextPage: blocksList.isFetchingNextPage,
                fetchMore: () => {
                    void blocksList.fetchNextPage();
                },
            }, columns: 2, groupBy: viewMode === "chronological" ? "day" : "none", onOpenCollection: handleOpenChildCollection, surfaceKey: `collection:${collectionId}`, analyticsSurface: "collection", pinScope: { type: "collection", id: collectionId }, pinSurface: "default", createStackOnDropParentId: allowStackOnDrop }));
    })();
    const wrapperClass = ["app-collection", hasSelection ? "app-collection--has-selection" : ""]
        .filter(Boolean)
        .join(" ");
    return (_jsxs("div", { className: wrapperClass, children: [header, _jsxs("div", { className: "app-collection-filter", children: [_jsx(FaIcon, { icon: faMagnifyingGlass, className: "app-collection-filter__icon" }), _jsx("input", { ref: filterInputRef, type: "text", className: "app-collection-filter__input", placeholder: "Filter stacks and blocks...", value: filterQuery, maxLength: MAX_COLLECTION_FILTER_QUERY_LENGTH, onChange: (e) => setFilterQuery(e.target.value), onKeyDown: (e) => {
                            if (e.key !== "Escape")
                                return;
                            if (filterQuery.length > 0) {
                                e.preventDefault();
                                setFilterQuery("");
                                return;
                            }
                            e.preventDefault();
                            e.currentTarget.blur();
                        }, "aria-label": "Filter stacks and blocks" })] }), body, isSettingsOpen && collection ? (_jsx(CollectionSettingsModal, { collection: collection, onClose: () => setIsSettingsOpen(false), onCollectionUpdated: handleCollectionUpdated })) : null, isChatOpen && (_jsx(CollectionChatSidebar, { collectionId: collectionId, collectionTitle: headerTitle, onClose: () => setIsChatOpen(false) }))] }));
});
// ---------------------------------------------------------------------------
// Routed entity content — branches by entity layer + auto-upgrades URL
// ---------------------------------------------------------------------------
//
// `/g/:id`, `/collection/:id`, and `/group/:id` all hit this component.
// Layer-0 entities (Groups) render `GroupViewContainer`; everything
// else (layer-1 Collections, Stacks) renders `CollectionDetailTabContent`.
//
// The layer comes from `collectionStore.useFindById`, which reads the
// live cache. On a cold load (deeplink straight to an unfetched
// entity), the cache is empty for one render frame — `CollectionDetailTabContent`
// is the safe default while we wait, since its own loading state is
// indistinguishable from a layer-1 collection's loading state, and we'd
// rather render a brief spinner than guess wrong.
//
// URL upgrade: when the user lands at the member-only `/g/{id}` URL (or
// a legacy ID-based path) AND the live cache has enough context to
// build a public slug URL, we rewrite the URL bar via `replace`. This
// keeps in-app navigation, refreshes, and external `/g/{id}` deeplinks
// all converging on the prettiest URL the cache can support. The
// `replace` (not `push`) keeps the browser back stack clean: hitting
// "Back" returns to the page before the entity, not to the unupgraded
// URL it briefly occupied.
const RoutedEntityContent = memo(({ entityId }) => {
    const navigate = useNavigate();
    const buildCollectionUrl = useCollectionUrlBuilder();
    const collection = collectionStore.useFindById(entityId);
    useEffect(() => {
        if (collection === undefined)
            return;
        const pretty = buildCollectionUrl(collection);
        if (pretty === window.location.pathname)
            return;
        // Only upgrade away from member-only / legacy entity routes —
        // never overwrite a URL the user already chose (e.g. a slug URL
        // that happens to alias the same entity).
        const currentIsEntityRoute = window.location.pathname.startsWith("/g/") ||
            window.location.pathname.startsWith("/collection/") ||
            window.location.pathname.startsWith("/group/");
        if (currentIsEntityRoute === false)
            return;
        navigate(pretty, { replace: true });
    }, [collection, navigate, buildCollectionUrl]);
    if (collection?.layerIndex === COLLECTION_LAYER.group) {
        return _jsx(GroupViewContainer, { groupId: entityId });
    }
    return _jsx(CollectionDetailTabContent, { collectionId: entityId });
});
// ---------------------------------------------------------------------------
// Resolver content — /b/{slug}, /{name}, /{name}/{collectionSlug}
// ---------------------------------------------------------------------------
//
// These three screens were standalone redirect-to-legacy-URL components
// before. They now live inside AppScreen so that:
//
//   1. The URL bar stays on the pretty slug URL (no redirect-to-legacy
//      defeating the rename).
//   2. The full app chrome (sidebar, omnibar, nav) renders during the
//      resolver fetch — the screens used to be unstyled centered text.
//   3. There's a single set of "is the user logged in?" gates,
//      shortcut hooks, modals, etc. — no parallel resolver shells.
const ResolverPlaceholder = memo(({ status, loadingLabel, notFoundLabel }) => {
    if (status === "loading") {
        return (_jsxs("div", { className: "loading-spinner-container", children: [_jsx(FaIcon, { icon: faSpinnerThird, className: "loading-spinner" }), loadingLabel !== "" && (_jsx("span", { className: "app-screen__status", children: loadingLabel }))] }));
    }
    if (status === "not-found") {
        return (_jsxs("p", { className: "app-screen__status", children: [notFoundLabel, " ", _jsx(Link, { to: "/", children: "Go home" })] }));
    }
    return (_jsxs("p", { className: "app-screen__status", children: ["Couldn't load. ", _jsx(Link, { to: "/", children: "Go home" })] }));
});
/**
 * `/b/{slug}` content. Resolves the block, activates its detail modal,
 * and replaces the URL with the containing collection's canonical URL
 * (slug-based when the cache has the parent + owner context to build
 * one, member-only `/g/{collectionId}` otherwise). Keeps the
 * modal-on-collection UX from the old `BlockDeeplinkScreen` but drops
 * the legacy `/collection/{id}` path it used to emit.
 */
const BlockDeeplinkContent = memo(({ blockSlug }) => {
    const navigate = useNavigate();
    const app = useApp();
    const buildUrlForCollectionId = useUrlForCollectionId();
    const [status, setStatus] = useState("loading");
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const result = await resolveBlockBySlug(blockSlug);
                if (cancelled)
                    return;
                if (result.type === "not-found") {
                    setStatus("not-found");
                    return;
                }
                app.setActiveBlock(result.block.id);
                navigate(buildUrlForCollectionId(result.block.collectionId), { replace: true });
            }
            catch {
                if (!cancelled)
                    setStatus("error");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [blockSlug, app, navigate, buildUrlForCollectionId]);
    return (_jsx(ResolverPlaceholder, { status: status, loadingLabel: "Loading block\u2026", notFoundLabel: "Block not found." }));
});
/**
 * `/{name}` content. Resolves the name segment to a profile, group, or
 * reserved keyword. Profiles render a placeholder in-place; groups
 * render `GroupViewContainer` in-place (no redirect to `/group/{id}`);
 * reserved/not-found shows the standard not-found message.
 *
 * Implicit groups have no public name and cannot be reached via this
 * route — the resolver returns `not-found` for them, which is correct.
 */
const NameResolvedContent = memo(({ name }) => {
    const [state, setState] = useState({ status: "loading" });
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const result = await resolveName(name);
                if (cancelled)
                    return;
                switch (result.type) {
                    case "user":
                        setState({ status: "user", data: result });
                        return;
                    case "group":
                        setState({ status: "group", groupId: result.group.id });
                        return;
                    case "reserved":
                    case "not-found":
                        setState({ status: "not-found" });
                        return;
                }
            }
            catch {
                if (!cancelled)
                    setState({ status: "error" });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [name]);
    if (state.status === "group") {
        return _jsx(GroupViewContainer, { groupId: state.groupId });
    }
    if (state.status === "user") {
        return (_jsxs("div", { className: "app-screen__status", style: { gap: 8, display: "flex", flexDirection: "column", alignItems: "center" }, children: [_jsxs("div", { style: { fontSize: 24, fontWeight: 600 }, children: ["@", state.data.account.username ?? name] }), _jsx("div", { style: { fontSize: 14, color: "var(--color-text-secondary, #666)" }, children: "Profile pages are coming soon." }), _jsx(Link, { to: "/", style: { marginTop: 16 }, children: "Go home" })] }));
    }
    return (_jsx(ResolverPlaceholder, { status: state.status === "loading" ? "loading" : state.status === "not-found" ? "not-found" : "error", loadingLabel: "Loading\u2026", notFoundLabel: `Nothing found for "${name}".` }));
});
/**
 * `/{name}/{collectionSlug}` content. Resolves the (parent, slug) pair
 * to a layer-1 collection ID and renders `CollectionDetailTabContent`
 * **in place** — the URL bar keeps showing the pretty slug URL instead
 * of redirecting to the legacy `/collection/{id}` path.
 *
 * Resolution is tiered to avoid a network round-trip on the common
 * "the user is a member" case:
 *
 *   1. Fast path — match `(name, slug)` against the local collection
 *      cache (`useFindCollectionBySlug`). Hits when the user navigated
 *      here from inside the app, or when AppScreen auto-upgraded a
 *      `/g/{id}` URL into its slug form. Renders instantly with no
 *      visible loading state.
 *
 *   2. Slow path — fall back to `GET /v1/public/resolve/:name/:slug`
 *      for slug URLs we don't recognize locally (e.g. anonymous
 *      visitor following a shared link, or a member viewing a
 *      collection that hasn't been loaded into the local cache yet).
 */
const SlugResolvedCollectionContent = memo(({ name, collectionSlug }) => {
    const cachedCollection = useFindCollectionBySlug(name, collectionSlug);
    const resolverQuery = useQuery({
        queryKey: ["public.resolveCollection", name, collectionSlug],
        queryFn: () => resolveCollection(name, collectionSlug),
        staleTime: 60_000,
        enabled: cachedCollection === undefined,
    });
    if (cachedCollection !== undefined) {
        return _jsx(CollectionDetailTabContent, { collectionId: cachedCollection.id });
    }
    if (resolverQuery.isLoading) {
        return (_jsx(ResolverPlaceholder, { status: "loading", loadingLabel: "", notFoundLabel: "" }));
    }
    if (resolverQuery.isError || resolverQuery.data === undefined) {
        return (_jsx(ResolverPlaceholder, { status: "error", loadingLabel: "", notFoundLabel: "" }));
    }
    if (resolverQuery.data.type === "not-found") {
        return (_jsx(ResolverPlaceholder, { status: "not-found", loadingLabel: "", notFoundLabel: "Collection not found." }));
    }
    return _jsx(CollectionDetailTabContent, { collectionId: resolverQuery.data.collection.id });
});
/**
 * `/c/{slug}` content. Resolves a standalone (parent-less, layer-1)
 * collection by its globally-unique slug — the slug carries its own
 * random suffix so no parent / owner context is needed.
 *
 * Tiered exactly like `SlugResolvedCollectionContent`:
 *
 *   1. Fast path — `useFindStandaloneCollectionBySlug` against the
 *      local cache. Hits for members navigating internally or after
 *      AppScreen auto-upgrades a `/g/{id}` URL.
 *   2. Slow path — `GET /v1/public/collection/:slug` for slug URLs we
 *      don't recognize locally (anonymous visitors of a published
 *      collection; members on a cold cache).
 */
const SlugResolvedStandaloneCollectionContent = memo(({ slug }) => {
    const cachedCollection = useFindStandaloneCollectionBySlug(slug);
    const resolverQuery = useQuery({
        queryKey: ["public.resolveStandaloneCollection", slug],
        queryFn: () => resolveStandaloneCollection(slug),
        staleTime: 60_000,
        enabled: cachedCollection === undefined,
    });
    if (cachedCollection !== undefined) {
        return _jsx(CollectionDetailTabContent, { collectionId: cachedCollection.id });
    }
    if (resolverQuery.isLoading) {
        return (_jsx(ResolverPlaceholder, { status: "loading", loadingLabel: "", notFoundLabel: "" }));
    }
    if (resolverQuery.isError || resolverQuery.data === undefined) {
        return (_jsx(ResolverPlaceholder, { status: "error", loadingLabel: "", notFoundLabel: "" }));
    }
    if (resolverQuery.data.type === "not-found") {
        return (_jsx(ResolverPlaceholder, { status: "not-found", loadingLabel: "", notFoundLabel: "Collection not found." }));
    }
    return _jsx(CollectionDetailTabContent, { collectionId: resolverQuery.data.collection.id });
});
// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------
/**
 * Branch the rendered content on the current route. The router collapses
 * many URL shapes into one of a few semantic "what should we show here"
 * branches, so this function reads from a single source of truth
 * (`useRouteEntity`) instead of N independent `useParams` calls in N
 * sibling components.
 */
const renderRouteContent = (entity) => {
    switch (entity.type) {
        case "home":
            return _jsx(StreamTabContent, {});
        case "collectionsTab":
            return _jsx(CollectionsTabContent, {});
        case "entity":
            return _jsx(RoutedEntityContent, { entityId: entity.entityId });
        case "transientCollection":
            return _jsx(SmartCollectionTabContent, { transientId: entity.transientId });
        case "blockDeeplink":
            return _jsx(BlockDeeplinkContent, { blockSlug: entity.blockSlug });
        case "standaloneCollection":
            return _jsx(SlugResolvedStandaloneCollectionContent, { slug: entity.slug });
        case "name":
            return _jsx(NameResolvedContent, { name: entity.name });
        case "collectionBySlug":
            return (_jsx(SlugResolvedCollectionContent, { name: entity.name, collectionSlug: entity.collectionSlug }));
        case "unknown":
            return _jsx(StreamTabContent, {});
    }
};
// Subscribes to `activeBlockId` in isolation so opening/closing the
// block-detail modal doesn't bounce the entire `AppScreenContent`
// subtree (AppNav, Stream, OmnibarHost, DraftBar,
// MultiselectActionBar, etc.). Before this gate lived as a sibling,
// the `hasActiveBlock` read lived inside `AppScreenContent` and a
// click on any card forced a ~35 ms reconciliation of the whole
// screen because the parent's JSX references changed.
//
// PRE-MOUNT STRATEGY
//
// The gate also burns the modal's first-mount cost up-front, after
// AppScreen has settled. Without this pre-mount, the FIRST click
// on any card in a session had to pay all of:
//
//   - lazy-chunk eval (the `block-detail-modal` chunk, which the
//     `loadBlockDetailModal` prefetch tries to warm by 2 s but
//     still loses races to fast clickers — and even when warm,
//     the chunk transitively pulls in the in-house emoji picker's
//     540 KB `emojibase-data/en/compact.json` plus a module-scope
//     IIFE that buckets + sorts ~1800 entries),
//   - React mount of the modal's ~10-component subtree
//     (`BlockDetailModal` + 5 toolbar / popover containers +
//     `AnnotationViewContainer` + `InlineAnnotationsLayer` +
//     `AnnotationAuthorAvatars` + `BlockView` for the active
//     card),
//   - first-time CSS application for ~14 SCSS files arriving on
//     the document together (modal, edit-shell, comments-rail,
//     emoji-picker, custom-color-popover, draw-actions-toolbar,
//     drawing-canvas, reaction-popover, selection-highlight,
//     selection-toolbar, side-toolbar, stamp-popover, toolbar),
//   - 7 hooks firing their first `useEffect` (including
//     `installDrawingCursors()`, which writes the
//     `--drawing-cursor-move` SVG data URI onto `<html>`, plus a
//     handful of document-level `mouseup` / `keydown` /
//     `pointerdown` listeners), and
//   - a double-`requestAnimationFrame` FLIP commit before the
//     open transition can even start.
//
// Every subsequent click pays none of that — modules are cached,
// CSS is already on the document, listeners are already
// installed, the idempotent cursor install is a no-op — which is
// why the very first click of a session felt uniquely slow while
// every following click felt instant.
//
// `BlockDetailModalContainer` was already built to render at
// `activeBlockId === undefined`: every per-block hook gates
// internally (`if (!activeBlockId) return;` on document-level
// listeners, `useBlockDetailSelection({ enabled: false })`'s
// early exit, the `activeBlock` `useMemo` returning `undefined`),
// and the `BlockDetailModal` returns no visible chrome — the
// root `<div class="block-detail-modal">` is `position: fixed;
// inset: 0; pointer-events: none; backdrop-filter: blur(0px)`
// with a transparent background, so it's invisible and
// click-through. The dormant pre-mount costs effectively nothing
// at rest but transparently absorbs every line item above during
// an idle window, leaving a real click to flip a single store
// value and have the open animation start on the same frame.
//
// `isPrewarmed` flips once after `MODAL_PREFETCH_DELAY_MS` and
// never flips back; `hasActiveBlock` short-circuits the wait so
// a user who clicks a card before the timer fires still gets
// the modal mounted immediately (they pay the old cost; the
// pre-mount is a best-effort warm-up, not a hard gate). From
// then on the container stays mounted for the rest of the
// AppScreen lifecycle.
//
// We deliberately use a plain `setTimeout` instead of
// `requestIdleCallback`: on a busy main thread (stream
// hydration, ingest polling, ResizeObservers firing during
// bootstrap) rIC can slip well past its `timeout`, so the
// "fires after the bootstrap settles" guarantee in practice
// meant "fires whenever the browser feels like it" — sometimes
// not before the user's first click. A fixed delay is
// predictable and still well after first paint.
const BlockDetailModalGate = memo(() => {
    const app = useApp();
    const hasActiveBlock = app.useStore((s) => s.ui.activeBlockId !== undefined);
    const [isPrewarmed, setIsPrewarmed] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined")
            return;
        const handle = window.setTimeout(() => setIsPrewarmed(true), MODAL_PREFETCH_DELAY_MS);
        return () => window.clearTimeout(handle);
    }, []);
    if (hasActiveBlock === false && isPrewarmed === false)
        return undefined;
    return (_jsx(React.Suspense, { fallback: undefined, children: _jsx(BlockDetailModalContainer, {}) }));
});
// Ancestor wrapper that owns the single `s.ui.isMultiselectMode`
// subscription for the entire app. The flag drives a top-level CSS
// class (`.app-screen--multiselect`) which downstream surfaces use
// to cascade their multiselect-mode appearance (selection overlays,
// suppressed pin buttons, cursor overrides, etc.) without each tile
// having to subscribe individually. Previously every visible block
// tile mounted its own `app.useStore((s) => s.ui.isMultiselectMode)`
// — flipping the flag on long-press caused N (re)renders across the
// feed. Hoisting the subscription here means exactly ONE component
// re-renders on the flag flip; the rest of the tree is reconciled as
// stable JSX (children prop identity is preserved because
// AppScreenContent itself doesn't re-render).
const AppScreenRoot = memo(({ children }) => {
    const app = useApp();
    const isMultiselectMode = app.useStore((s) => s.ui.isMultiselectMode);
    const className = isMultiselectMode ? "app-screen app-screen--multiselect" : "app-screen";
    return _jsx("div", { className: className, children: children });
});
AppScreenRoot.displayName = "AppScreenRoot";
const AppScreenContent = () => {
    const { user, isLoading: authLoading } = useAuth();
    useWipBorderShortcut();
    // ⌘F / ⌘D / ⌘E (omnibar focus + cycle) are mounted higher in the
    // tree at `LoggedInShell` so the bindings stay active on every
    // logged-in route — including dev / components / experimental
    // screens that don't render `<AppScreen />`. See
    // `use-omnibar-focus-shortcut.ts`.
    const entity = useRouteEntity();
    const prevRouteTypeRef = useRef("initial");
    useEffect(() => {
        const from = prevRouteTypeRef.current;
        if (from === entity.type)
            return;
        track({ name: "route.viewed", props: { route_type: entity.type, from_route_type: from } });
        prevRouteTypeRef.current = entity.type;
    }, [entity.type]);
    // Prefetch the block-detail modal chunk after a fixed delay.
    //
    // The modal lives behind a `React.lazy` boundary so the cold-load
    // bundle stays slim, but the consequence is that the FIRST card
    // click of a session pays the full chunk-fetch latency (network +
    // parse + execute) before the open animation can start. On a slow
    // connection this is visible as a "click did nothing for half a
    // second" stall. Prefetching here closes that gap: by the time the
    // user clicks, the chunk is already in webpack's module cache and
    // `Suspense` resolves on the same render tick.
    //
    // We deliberately use a plain `setTimeout` instead of
    // `requestIdleCallback`: on a busy main thread (stream hydration,
    // ingest polling, ResizeObservers firing during bootstrap) rIC can
    // slip well past its `timeout`, so the "fires after the bootstrap
    // settles" guarantee in practice meant "fires whenever the browser
    // feels like it" — sometimes not before the user's first click.
    // A fixed 2 s delay is predictable and still well after first
    // paint. Failure to load is logged but otherwise silent — the
    // click path still works via `Suspense`, the user just pays the
    // fetch cost on click as they would have without the prefetch.
    useEffect(() => {
        if (typeof window === "undefined")
            return;
        const handle = window.setTimeout(() => {
            void loadBlockDetailModal().catch((e) => {
                console.warn("[app-screen] failed to prefetch block-detail-modal:", e);
            });
        }, MODAL_PREFETCH_DELAY_MS);
        return () => window.clearTimeout(handle);
    }, []);
    const content = renderRouteContent(entity);
    if (authLoading) {
        return (_jsx("div", { className: "app-screen", children: _jsx("p", { className: "app-screen__status", children: "Authenticating..." }) }));
    }
    if (!user) {
        return (_jsx("div", { className: "app-screen", children: _jsxs("p", { className: "app-screen__status", children: ["Not signed in. ", _jsx(Link, { to: "/", children: "Go home" })] }) }));
    }
    return (_jsxs(AppScreenRoot, { children: [_jsx(ScreenContainer, { children: _jsx(MainSlate, { header: _jsx(SlateHeader, { children: _jsx(AppNav, {}) }), content: _jsx(SlateContent, { children: content }), omnibar: _jsx(OmnibarHost, {}) }) }), _jsx(BlockDetailModalGate, {}), _jsx(MultiselectActionBar, {})] }));
};
export const AppScreen = AppScreenContent;
